import type * as fsType from 'fs/promises'

import { exponantialBackOff } from '@climatepartner/unified-api-exponantial-backoff'
import {
  HttpHeaders,
  MimeTypes,
  bearerAuthHeader,
} from '@climatepartner/unified-api-lib-http-utils'

import { StsBaseService } from '../gen/service/sts-base-service'
import { CreateOrderErrorCode, UnifiedApiError } from '../types'

// Browser support
let fs: typeof fsType
if (typeof window === 'undefined') {
  fs = require('fs/promises')
}

interface Token {
  expires: number
  token: string
}

export class StsService extends StsBaseService {
  #cpinsToken?: Token

  constructor(
    private longtermToken: string,
    private authorizationStoragePath?: string,
  ) {
    super()
  }

  /**
   * get the access token. If token is expired a new token will be fetched
   * and stored.
   */
  getToken = async (force = false): Promise<string> => {
    if (this.authorizationStoragePath) {
      this.#cpinsToken = await this.#readTokenFromDisk(
        this.authorizationStoragePath,
      )
    }
    if (!force && this.#validateToken()) {
      return this.#cpinsToken!.token
    }

    const response = await exponantialBackOff(
      async () => {
        try {
          return await this.token(
            { disableCache: force ? 'true' : undefined },
            {},
            async request => {
              request.header = {
                [HttpHeaders.AUTHORIZATION]: bearerAuthHeader(
                  this.longtermToken,
                ),
                [HttpHeaders.CONTENT_TYPE]: MimeTypes.APPLICATION_JSON,
              }
              return request
            },
          )
        } catch (e: UnifiedApiError | any) {
          // The default response interceptor configured in sdk index.ts will throw an UnifiedApiError if error code is >= 400
          if (e instanceof UnifiedApiError) {
            throw e
          }

          throw new UnifiedApiError(
            0,
            CreateOrderErrorCode.INTERNAL_ERROR,
            e.message,
          )
        }
      },
      {
        numberOfAttempts: 8, // 14.3s total maximum delay (Explanation in ../order/index.ts:#createOrderSync)
        maximumDelay: 4000,
        startingDelay: 100,
      },
    )

    await this.#updateToken({
      expires: Date.now() + response.result.expires_in * 1000 - 60000,
      token: response.result.access_token,
    })

    return response.result.access_token
  }

  #readTokenFromDisk = async (
    tokenStoragePath: string,
  ): Promise<Token | undefined> => {
    // Browser support
    if (typeof window === 'undefined') {
      const exists = await fs.access(tokenStoragePath).then(
        () => true,
        () => false,
      )
      if (exists) {
        const token = await fs.readFile(tokenStoragePath, 'utf8')
        if (token) {
          return JSON.parse(token)
        }
      }
    }
  }

  #updateToken = async (token: Token) => {
    // Browser support
    if (typeof window === 'undefined') {
      if (this.authorizationStoragePath) {
        await fs.writeFile(this.authorizationStoragePath, JSON.stringify(token))
      }
    }
    this.#cpinsToken = token
  }

  #validateToken = (): boolean => {
    if (this.#cpinsToken) {
      return this.#cpinsToken.expires - Date.now() >= 0
    }

    return false
  }
}
